import { useMobileDetect } from "@/lib/hooks/useMobileDetect";
import type React from "react";
import { useRef, useState } from "react";

import { X } from "lucide-react";
import Image from "next/image";
import { Camera, type CameraType } from "./react-camera-pro";

export function base64ToFile(base64String: string, filename: string) {
  // Extract the base64 data from the string
  const [, data] = base64String.split(",");

  // Convert base64 to binary
  const binaryData = atob(data);

  // Create an array buffer from the binary data
  const arrayBuffer = new ArrayBuffer(binaryData.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < binaryData.length; i++) {
    uint8Array[i] = binaryData.charCodeAt(i);
  }

  // Create a Blob from the array buffer
  const blob = new Blob([arrayBuffer], { type: "image/jpeg" });

  // Create a File object from the Blob
  return new File([blob], filename, { type: "image/jpeg" });
}

const CameraContainer = ({
  documentType,
  setCameraStatus,
  setImageFile,
  questionText,
}: {
  documentType: "Insurance" | "ID";
  setCameraStatus: React.Dispatch<React.SetStateAction<boolean>>;
  setImageFile: (files: File[]) => void;
  questionText: string;
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [showImage, setShowImage] = useState<boolean>(false);
  const camera = useRef<CameraType>(null);
  const [signedURL, setSignedURL] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMobile, isDesktop } = useMobileDetect();

  const [activeDeviceId, setActiveDeviceId] = useState<string | undefined>(
    undefined,
  );

  //https://stackoverflow.com/questions/4998908/convert-data-uri-to-file-then-append-to-formdata

  const onSubmit = async () => {
    setIsSubmitting(true);

    const formData = new FormData();

    try {
      if (image !== null) {
        setImageFile([base64ToFile(image, `${questionText}.jpg`)]);

        // setImageFile(image);
        formData.append("file", image);
        formData.append("documentType", documentType);

        setIsSubmitting(false);
        // Need to extract OCR logic out of the camera component

        // const response = await fetch('/api/ocr', {
        //   method: 'POST',
        //   body: formData,
        // }).then(async (res) => {
        //   const result = await res.json();
        //   setSignedURL(result);
        //   if (documentType === 'ID') {
        //     setter(result);
        //   } else {
        //     const parsedData = await parseForm(result.response);

        //     setter({
        //       potentialPayerName: parsedData[1][0],
        //       potentialID: parsedData[1][1],
        //     });
        //   }
        //   setIsSubmitting(false);
        // });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form
      className="bottom-0 left-0  h-[400px] rounded-[15px] relative w-[600px]"
      id="photoForm"
    >
      <div className="flex w-full h-full bg-black">
        {image ? (
          <div
            className="w-full h-full z-[100] absolute bg-black bg-contain bg-no-repeat bg-center"
            style={{ backgroundImage: `url(${image})` }}
            onClick={() => {
              setShowImage(!showImage);
            }}
          />
        ) : (
          <Camera
            ref={camera}
            aspectRatio="cover"
            numberOfCamerasCallback={(i) => i}
            facingMode={isMobile() ? "environment" : "user"}
            videoSourceDeviceId={activeDeviceId}
            errorMessages={{
              noCameraAccessible:
                "No camera device accessible. Please connect your camera or try a different browser.",
              permissionDenied:
                "Permission denied. Please refresh and give camera permission.",
              switchCamera:
                "It is not possible to switch camera to different one because there is only one video device accessible.",
              canvas: "Canvas is not supported.",
            }}
            videoReadyCallback={() => {
              console.log("Video feed ready.");
            }}
          />
        )}
      </div>
      <div className="absolute flex right-0 bottom-0 z-[110] w-full sm:h-1/5 lg:h-[75px] bg-black items-center justify-center box-border sm:flex-row lg:flex-col-reverse sm:p-2.5 lg:p-0">
        {image ? (
          <>
            <button
              type="button"
              className="w-[120px] h-10 rounded-full font-medium border-0 left-0 text-white underline bg-transparent absolute"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setImage(null);
              }}
            >
              Retake
            </button>
            <button
              className="w-[160px] h-10 rounded-full border-0 bg-superscriptBlue text-white bottom-0"
              type="button"
              disabled={isSubmitting}
              form="photoForm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSubmit();
              }}
            >
              {isSubmitting ? "...Submitting" : "Submit"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="w-[60px] h-[60px] rounded-full outline-none text-white bg-transparent z-[2] border-none opacity-100 hover:opacity-70"
            style={{ textShadow: "0px 0px 4px black" }}
            onClick={(e) => {
              if (camera.current) {
                e.preventDefault();
                const photo = camera.current.takePhoto();
                setImage(photo as string);
              }
            }}
          >
            <Image
              alt="Camera Icon"
              src="/icons/PhototakeIcon.svg"
              priority
              width={18}
              height={18}
              style={{
                width: "48px",
                height: "48px",
                marginLeft: "1px",
                zIndex: "1001",
              }}
            />
          </button>
        )}
      </div>

      <input style={{ display: "none" }} type="file" name="file" />

      <button
        className="absolute top-4 right-4 bg-transparent border-0 z-[1000]"
        type="button"
        onClick={() => setCameraStatus(false)}
      >
        <X />
      </button>
    </form>
  );
};

export default CameraContainer;
